import { useLoaderData } from "react-router";
import React from "react";
import TwoColumnLayout from "layout/TwoColumnLayout";
import { GenericDocumentV1DetailSection } from "../../documentV1/detail/GenericDocumentV1DetailSection";
import { isPanelVisible } from "utilities/panelUtils";
import UserTasksPanel from "userTask/list/app";
import NotesPanel from "note/list/app";
import EmailsPanel from "email/list/App";
import DocumentFromTemplatePanel from "DocumentFromTemplate/app";
import FilesPanel from "show/files/app";
import { UploadFilesConfig, uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import LinkShowApp from "document/linkshow/app";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import AreYouSureModal from "components/modals/AreYouSureModal";
import { DeleteDialog } from "components";
import PaymentsPanel, { usePaymentsByInvoiceQuery } from "invoice/makepayment/PaymentsPanel";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ProductsPanel, { useProductsByParentQuery } from "eSign/panelsegment/ProductsPanel";
import { routes } from "utilities/routing/generatedRoutes";
import moment from "moment";
import AdminOneInvoicePanel from "./AdminOneInvoicePanel";
import { useQueryClient } from "@tanstack/react-query";
import { getDocumentFromTemplateQueryKey } from "DocumentFromTemplate/app";
import { mapInvoiceToFormState } from "documentV1/detail/documentFormState";

export const InvoiceV1DetailApp = () => {
  const viewModel = useLoaderData<server.viewmodel.InvoiceV1DetailFrontModel>();
  const [invoice, setInvoice] = React.useState(viewModel.invoice);
  const [invoiceFormState, setInvoiceFormState] = React.useState(mapInvoiceToFormState(viewModel.invoice));
  const [euVatNumber, setEuVatNumber] = React.useState(viewModel.euVatNumber);
  const [reverseChargeAdvice, setReverseChargeAdvice] = React.useState(viewModel.reverseChargeAdvice);
  const [minInvoiceDate, setMinInvoiceDate] = React.useState(moment.utc(viewModel.minimumInvoiceDate));
  const [maxInvoiceDate, setMaxInvoiceDate] = React.useState(moment.utc(viewModel.maximumInvoiceDate));
  const [isLocking, setIsLocking] = React.useState(false);
  const [isUnlocking, setIsUnlocking] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const lock = async () => {
    const res = await window.CoreApi.Document.Lock(invoice.invoiceId)
    if (!res.data.hasErrors) {
      window.location.reload();
    }
    return res.data;
  }

  const unlock = async () => {
    const res = await window.CoreApi.Document.Unlock(invoice.invoiceId)
    if (!res.data.hasErrors) {
      window.location.reload();
    }
    return res.data;
  }

  const productsByParentQuery = useProductsByParentQuery("invoice", invoice.invoiceId, viewModel.productsPanel.data);
  const paymentsApi = usePaymentsByInvoiceQuery(invoice.invoiceId);
  const queryClient = useQueryClient();

  const canMakeCredit = site.permissions.invoice.canCreate
    && invoice.lifeCycle !== "draft"
    && productsByParentQuery.data != null
    && productsByParentQuery.data.total.sales != null // If user has no permission to see sales, this will be null
    && productsByParentQuery.data.total.sales.amount > 0;

  const refetchInvoice = async () => {
    const res = await window.CoreApi.Invoice.GetSidePanelData(invoice.invoiceId);
    setEuVatNumber(res.data.data.euVatNumber);
    setReverseChargeAdvice(res.data.data.reverseChargeAdvice);
    setMinInvoiceDate(moment.utc(res.data.data.minimumInvoiceDate));
    setMaxInvoiceDate(moment.utc(res.data.data.maximumInvoiceDate));
    setInvoice(res.data.data.invoice);
    setInvoiceFormState(mapInvoiceToFormState(res.data.data.invoice));

    queryClient.invalidateQueries(getDocumentFromTemplateQueryKey(invoice.invoiceId));
  };

  // When products change, the payments panel also has to adjust accordingly
  const onRefetchedProducts = () => {
    refetchInvoice();
    paymentsApi.refetch();
  }

  // When invoice currency changes, products and payments will change too
  const onRefetchProductsAndPayments = () => {
    productsByParentQuery.refetch();
    return paymentsApi.refetch();
  }

  // When linked shows change, the invoice might go from settled to unsettled so we need to refetch
  // The product/payment panels are auto-updated by react-query cache invalidation when shows change
  const onRefetchedShows = () => {
    refetchInvoice();
  }

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <SectionPanel.Section>
        <GenericDocumentV1DetailSection
          document={invoice}
          formState={invoiceFormState}
          onChange={setInvoiceFormState}
          products={productsByParentQuery.data}
          euVatNumber={euVatNumber}
          reverseChargeAdvice={reverseChargeAdvice}
          minSendDate={minInvoiceDate}
          maxSendDate={maxInvoiceDate}
          refetchDocument={refetchInvoice}
          refetchProducts={onRefetchProductsAndPayments}
          lastModifiedUser={viewModel.lastModifiedUser}
          artistRiders={[]}
        />
      </SectionPanel.Section>

      {invoice.lifeCycle !== "draft" && <PaymentsPanel canModify={viewModel.canModifyPayments} invoice={invoice} onRefetchedPayments={refetchInvoice} />}

      {isPanelVisible(viewModel.productsPanel.config) && <ProductsPanel
        useSectionPanel={false}
        documentId={invoice.invoiceId}
        documentType="invoice"
        productsPanel={viewModel.productsPanel}
        onRefetchedProducts={onRefetchedProducts}
      />}


      {isPanelVisible(viewModel.linkedUserTaskPanelConfig) && <UserTasksPanel
        mainArea="invoice"
        config={viewModel.linkedUserTaskPanelConfig}
      />}

      {isPanelVisible(viewModel.linkedNotePanelConfig) && <NotesPanel
        mainArea="invoice"
        config={viewModel.linkedNotePanelConfig}
      />}

      {isPanelVisible(viewModel.linkedEmailPanelConfig) && <EmailsPanel
        mainArea="invoice"
        emailTemplateType="invoiceEmailTemplate"
        config={viewModel.linkedEmailPanelConfig}
      />}

      {isPanelVisible(viewModel.generateDocumentPanelConfig) && <DocumentFromTemplatePanel
        itemId={invoice.invoiceId}
        api={window.CoreApi.Invoice.ExportOptionsForDetail}
        panelTitle={viewModel.generateDocumentPanelConfig.headerLabel}
      />}

      {isPanelVisible(viewModel.linkedFilePanelConfig) && <FilesPanel
        config={viewModel.linkedFilePanelConfig}
        uploadFiles={(files: File[], uploadConfig: UploadFilesConfig) => {
          return uploadFilesToCoreApi(
            window.CoreApi.File.UploadClientFiles,
            window.CoreApi.File.UploadClientFiles.absoluteUrl(),
            {
              files,
              mainArea: "invoice",
              parentId: invoice.invoiceId
            },
            uploadConfig
          );
        }}
        fetchFilesApi={(page, perPage) => window.CoreApi.Invoice.PaginatedFiles({ parentId: invoice.invoiceId, page, perPage })}
      />}

      {isPanelVisible(viewModel.linkedShowPanel.config) && <LinkShowApp
        linkedShowsPanel={viewModel.linkedShowPanel}
        documentType="invoice"
        defaultBookkeeping={invoice.bookkeeping}
        onShowsChange={onRefetchedShows}
      />}
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <GeneralOptionsPanel
        data={viewModel.optionsPanel}
        links={[
          { text: RESX.Buttons.ListView, visible: true, href: viewModel.listUrl },
          { text: RESX.InvoiceExport.Lock, visible: invoice.permissions.canLockUnlock && invoice.locked === "unlocked" && invoice.lifeCycle !== "draft", onClick: () => { setIsLocking(true); } },
          { text: RESX.InvoiceExport.Unlock, visible: invoice.permissions.canLockUnlock && invoice.locked === "locked", onClick: () => { setIsUnlocking(true); } },
          { text: RESX.Invoice.MakeCredit, visible: canMakeCredit, href: routes.invoiceV1_MakeCredit(invoice.invoiceId).absolute },
          { text: RESX.Buttons.btnCopy, visible: invoice.permissions.canModify, href: routes.invoiceV1_Copy(invoice.invoiceId).absolute },
          { text: RESX.Buttons.btnDelete, visible: invoice.permissions.canDelete, onClick: () => { setIsDeleting(true); } },
        ]}
        shortcutOrder={["product", "task", "note", "email", "file", "show"]}
      />

      {site.permissions.canAccessAdminOne && <AdminOneInvoicePanel viewModel={viewModel} />}
      {isLocking && <AreYouSureModal
        title={RESX.InvoiceExport.Lock}
        yesButtonColor="red"
        yesButtonLabel={RESX.InvoiceExport.Lock}
        onClose={() => setIsLocking(false)}
        onYes={lock}
      />}

      {isUnlocking && <AreYouSureModal
        title={RESX.InvoiceExport.Unlock}
        yesButtonColor="red"
        yesButtonLabel={RESX.InvoiceExport.Unlock}
        onClose={() => setIsUnlocking(false)}
        onYes={unlock}
      />}

      {isDeleting && <DeleteDialog
        isOpen
        handleClose={() => setIsDeleting(false)}
        onDelete={() => window.CoreApi.Invoice.Delete({ invoiceId: invoice.invoiceId }).then((r) => {
          if (r.data.hasErrors) {
            window.displayGenericModal(RESX.GeneralLabels.Delete, r.data.errors[0].message, "tiny", "red");
            setIsDeleting(false);
            return;
          }
          window.location.href = viewModel.listUrl;
        })}
        message={RESX.GeneralWarnings.DeleteSure}
        spinForever
      />}


    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>
}